import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Input, Spin, Empty } from "antd";
import axios from "axios";
import ProductCategoryCard from "./CategoryCard.js";
import "../../config";
import { useNavigate } from "react-router-dom";

const { Search } = Input;

const ProductCategory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [productCategory, setProductCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollRestored, setScrollRestored] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // First, try to restore scroll position immediately
    const savedScroll = sessionStorage.getItem("categoryListScroll");

    if (savedScroll && savedScroll !== "0") {
      const scrollY = parseInt(savedScroll, 10);
      window.scrollTo(0, scrollY);
    }

    setLoading(true);
    getListofProductCategory();
    document.title = "JOJ | Category List";

    // Only log user activity if token is present
    const tokenString = localStorage.getItem("token");
    if (tokenString) {
      logUserActivity();
    }

    // Throttled scroll handling to prevent too many saves
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollY =
          window.pageYOffset || document.documentElement.scrollTop;
        if (scrollY > 50) {
          // Increased threshold to avoid saving small scroll values
          sessionStorage.setItem("categoryListScroll", scrollY.toString());
        }
      }, 100);
    };

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);

      // FIXED: Only save scroll position on unmount if it's significantly greater than 50
      // and if there isn't already a larger value saved
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const currentSaved = parseInt(
        sessionStorage.getItem("categoryListScroll") || "0",
        10
      );

      if (scrollY > 50 && scrollY > currentSaved) {
        sessionStorage.setItem("categoryListScroll", scrollY.toString());
      }
    };
  }, []);

  // Scroll restoration after data loads (single attempt, once content has laid out)
  useEffect(() => {
    if (!loading && productCategory.length > 0 && !scrollRestored) {
      const restoreTimer = setTimeout(() => {
        const savedScroll = sessionStorage.getItem("categoryListScroll");
        const scrollY = savedScroll ? parseInt(savedScroll, 10) : 0;

        if (scrollY > 50) {
          window.scrollTo({ top: scrollY, behavior: "auto" });
        }

        setScrollRestored(true);
      }, 100);

      return () => clearTimeout(restoreTimer);
    }
  }, [loading, productCategory, scrollRestored]);

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      const jsondata = JSON.stringify({
        ScreenID: "1",
        DetailID: "0",
      });

      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };

      axios
        .request(config)
        .then((response) => {})
        .catch((error) => {});
    } catch (err) {}
  };

  // Combine all categories from all sections, then sort by SortingNo
  const combinedSortedCategories = useMemo(() => {
    return (productCategory || [])
      .flatMap((section) =>
        (section.data || []).map((cat) => ({
          ...cat,
          sectionTitle: section.title,
          SortingNumber: cat.SortingNo,
        }))
      )
      .sort((a, b) => (a.SortingNumber ?? 0) - (b.SortingNumber ?? 0));
  }, [productCategory]);

  async function getListofProductCategory() {
    setLoading(true);
    const tokenString = localStorage.getItem("token");

    try {
      let config;
      if (tokenString) {
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/ProductCategory/ListofProductCategory",
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        };
      } else {
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/PublicData/ListofProductCategory",
        };
      }

      axios
        .request(config)
        .then((response) => {
          setProductCategory(response.data);
          setLoading(false);
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem("token");
          }
          setLoading(false);
        });
    } catch (err) {
      setLoading(false);
    }
  }

  // Handle category card click
  const handleCategoryClick = useCallback(
    (category) => {
      const scrollY =
        window.pageYOffset || document.documentElement.scrollTop;

      // Always save the scroll position when user intentionally navigates
      sessionStorage.setItem("categoryListScroll", scrollY.toString());

      // Store category ID
      sessionStorage.setItem("CategoryID", category.ProductCategoryID);

      // Navigate
      navigate(`/shop/${category.CategoryName}/${category.ProductCategoryID}`);
    },
    [navigate]
  );

  const filteredCategories = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return combinedSortedCategories.filter((cat) =>
      cat.CategoryName.toLowerCase().includes(term)
    );
  }, [combinedSortedCategories, searchTerm]);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 20px 56px" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 28,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: -0.5,
              margin: 0,
            }}
          >
            Shop by Category
          </h1>
          <p style={{ margin: "4px 0 0", color: "#8c8c8c", fontSize: 14 }}>
            {combinedSortedCategories.length} categories available
          </p>
        </div>

        <Search
          placeholder="Search category..."
          onSearch={(value) => setSearchTerm(value)}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          style={{ maxWidth: 280, width: "100%" }}
        />
      </div>

      <Spin spinning={loading} tip="Loading categories...">
        <div style={{ minHeight: loading ? 240 : undefined }}>
          {!loading && filteredCategories.length === 0 ? (
            <Empty
              description="No categories found"
              style={{ padding: "80px 0" }}
            />
          ) : (
            <div className="category-grid">
              {filteredCategories.map((category, index) => (
                <ProductCategoryCard
                  key={category.ProductCategoryID}
                  category={category}
                  onClick={handleCategoryClick}
                  priority={index < 4}
                />
              ))}
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};

export default ProductCategory;
